/**
 * CartridgeSelectScreen - Provider selection with carousel UI
 * First screen shown to new users to select their project management tool
 */

import { useState, useEffect, useCallback } from 'react';
import { useGame } from '../context/GameContext';
import './CartridgeSelectScreen.css';

// Cartridge images
import jiraCartridge from '../assets/cartridges/jira_cartridge.png';
import linearCartridge from '../assets/cartridges/linear_cartridge.png';
import notionCartridge from '../assets/cartridges/notion_cartridge.png';
import trelloCartridge from '../assets/cartridges/trello_cartridge.png';

// Console images (split for sandwich effect)
import consoleTop from '../assets/console_top.png';
import consoleBody from '../assets/console-body.png';

interface Cartridge {
  id: string;
  name: string;
  image: string;
  active: boolean;
}

const CARTRIDGES: Cartridge[] = [
  { id: 'jira', name: 'Jira', image: jiraCartridge, active: true },
  { id: 'linear', name: 'Linear', image: linearCartridge, active: false },
  { id: 'notion', name: 'Notion', image: notionCartridge, active: false },
  { id: 'trello', name: 'Trello', image: trelloCartridge, active: false },
];

export function CartridgeSelectScreen() {
  const { actions } = useGame();
  const [currentIndex, setCurrentIndex] = useState(0); // Jira is at index 0
  const [isAnimating, setIsAnimating] = useState(false);
  const [isInserting, setIsInserting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const currentCartridge = CARTRIDGES[currentIndex];

  // Navigate carousel
  const goToPrevious = useCallback(() => {
    if (isAnimating || isInserting) return;
    setCurrentIndex((prev) => (prev - 1 + CARTRIDGES.length) % CARTRIDGES.length);
  }, [isAnimating, isInserting]);

  const goToNext = useCallback(() => {
    if (isAnimating || isInserting) return;
    setCurrentIndex((prev) => (prev + 1) % CARTRIDGES.length);
  }, [isAnimating, isInserting]);

  // Handle cartridge selection
  const handleSelect = () => {
    if (isAnimating || isInserting) return;

    if (!currentCartridge.active) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      return;
    }

    setIsInserting(true);
    setIsAnimating(true);

    // Save provider to localStorage
    localStorage.setItem('selectedProvider', currentCartridge.id);

    // Animation duration then transition to Boot
    setTimeout(() => {
      actions.setPhase('BOOT');
    }, 2000);
  };

  // Handle side cartridge click
  const handleSideClick = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      goToPrevious();
    } else {
      goToNext();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isInserting) return;

      switch (e.code) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
        case 'KeyD':
          goToNext();
          break;
        case 'Enter':
        case 'Space':
        case 'KeyA':
          handleSelect();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext, isInserting]);

  // Get cartridge at relative position
  const getCartridgeAt = (offset: number): Cartridge => {
    const index = (currentIndex + offset + CARTRIDGES.length) % CARTRIDGES.length;
    return CARTRIDGES[index];
  };

  return (
    <div className={`screen cartridge-select-screen ${isAnimating ? 'animating' : ''}`}>
      {/* Pixel Art Sprites */}
      <div className="pixel-sprites">
        <div className="pixel-sprite sprite-coin sprite-coin-1" />
        <div className="pixel-sprite sprite-coin sprite-coin-2" />
        <div className="pixel-sprite sprite-coin sprite-coin-3" />
        <div className="pixel-sprite sprite-heart sprite-heart-1" />
        <div className="pixel-sprite sprite-heart sprite-heart-2" />
        <div className="pixel-sprite sprite-star sprite-star-1" />
        <div className="pixel-sprite sprite-star sprite-star-2" />
        <div className="pixel-sprite sprite-star sprite-star-3" />
        <div className="pixel-sprite sprite-star sprite-star-4" />
        <div className="pixel-sprite sprite-cloud sprite-cloud-1" />
        <div className="pixel-sprite sprite-cloud sprite-cloud-2" />
        <div className="pixel-sprite sprite-cloud sprite-cloud-3" />
      </div>

      {/* Title */}
      <div className="cartridge-header">
        <h1 className="cartridge-title">
          <span className="title-easy">EASY</span>
          <span className="title-daily">DAILY</span>
        </h1>
        <p className="cartridge-subtitle">GAMIFIED STANDUP</p>
      </div>

      {/* Console insertion animation (sandwich effect) */}
      {isInserting && (
        <div className="console-container">
          {/* Layer 1: Console top (behind cartridge) */}
          <img
            src={consoleTop}
            alt="Console top"
            className="console-top"
          />

          {/* Layer 2: Cartridge (middle - slides into slot) */}
          <img
            src={currentCartridge.image}
            alt="Inserting cartridge"
            className="inserting-cartridge"
          />

          {/* Layer 3: Console body (in front of cartridge) */}
          <img
            src={consoleBody}
            alt="Console body"
            className="console-body"
          />
        </div>
      )}

      {/* Insert cartridge label */}
      {!isInserting && (
        <>
          <p className="insert-label">SELECT CARTRIDGE</p>

          {/* Carousel */}
          <div className="carousel">
            {/* Left arrow */}
            <button className="carousel-arrow carousel-arrow-left" onClick={goToPrevious}>
              ◀
            </button>

            {/* Cartridges */}
            <div className="carousel-track">
              {/* Left */}
              <button
                className="carousel-item carousel-item-left"
                onClick={() => handleSideClick('left')}
              >
                <img
                  src={getCartridgeAt(-1).image}
                  alt={getCartridgeAt(-1).name}
                  className="cartridge-image"
                />
                {!getCartridgeAt(-1).active && (
                  <span className="coming-soon-badge">COMING SOON</span>
                )}
              </button>

              {/* Center (current) */}
              <button
                className={`carousel-item carousel-item-center ${!currentCartridge.active ? 'locked' : ''}`}
                onClick={handleSelect}
              >
                <img
                  src={currentCartridge.image}
                  alt={currentCartridge.name}
                  className="cartridge-image"
                />
                {currentCartridge.active && (
                  <span className="active-badge">★ READY ★</span>
                )}
                {!currentCartridge.active && (
                  <span className="coming-soon-badge">COMING SOON</span>
                )}
              </button>

              {/* Right */}
              <button
                className="carousel-item carousel-item-right"
                onClick={() => handleSideClick('right')}
              >
                <img
                  src={getCartridgeAt(1).image}
                  alt={getCartridgeAt(1).name}
                  className="cartridge-image"
                />
                {!getCartridgeAt(1).active && (
                  <span className="coming-soon-badge">COMING SOON</span>
                )}
              </button>
            </div>

            {/* Right arrow */}
            <button className="carousel-arrow carousel-arrow-right" onClick={goToNext}>
              ▶
            </button>
          </div>

          {/* Controls hint */}
          <div className="carousel-controls">
            <span>[◀ ▶] Browse</span>
            <span>[ENTER] Select</span>
          </div>
        </>
      )}

      {/* Toast notification */}
      {showToast && (
        <div className="toast">Coming Soon!</div>
      )}

      {/* Footer */}
      <footer className="cartridge-footer">
        fxbravo with <span className="heart">❤️</span>
      </footer>
    </div>
  );
}

export default CartridgeSelectScreen;
